import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableColumn } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { AuditTableQueryParam } from 'libs/end-to-end/src/audits';
import { GetAuditsResponseDto } from 'libs/end-to-end/src/audits';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { SecondaryButtonLightBg } from 'apps/sigma2-ui/src/components/SecondaryButtonLightBg';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { PartialDateRange } from 'libs/end-to-end/src/date-range';
import { S2DateRangePicker } from 'apps/sigma2-ui/src/components/DateRangePicker';

/**
 * AuditsPage component
 *
 * This component renders the Audits page, allowing super admins to view and search through audits.
 */
export const AuditsPage = () => {
  const language = useLanguage();
  const { userAuthenticatedApiCall } = useUser();

  const [selectedAuditTable, setSelectedAuditTable] =
    useState<AuditTableQueryParam>('users');
  const [selectedPageSize, setSelectedPageSize] = useState('25');
  const [selectedDateRange, setSelectedDateRange] =
    useState<PartialDateRange>();
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [selectedActorUserId, setSelectedActorUserId] = useState('');

  const [isSearching, setIsSearching] = useState(false);
  const [auditsResponse, setAuditsResponse] =
    useState<GetAuditsResponseDto | null>(null);

  // Add new state for error modal
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Callback to get the URL for querying audits
   */
  const auditQueryUrl = useMemo(() => {
    const params = new URLSearchParams();

    params.set('auditTable', selectedAuditTable);

    if (selectedPageSize !== 'all') {
      params.set('pageSize', selectedPageSize);
    }

    if (selectedDateRange) {
      if (selectedDateRange.start) {
        params.set('dateRange[from]', selectedDateRange.start.toISOString());
      }
      if (selectedDateRange.end) {
        params.set('dateRange[to]', selectedDateRange.end.toISOString());
      }
    }

    if (selectedSourceId.trim().length > 0) {
      params.set('sourceId', selectedSourceId);
    }

    if (selectedActorUserId.trim().length > 0) {
      params.set('actorUserId', selectedActorUserId);
    }

    return `${getApiBaseUrl()}/audits?${params.toString()}`;
  }, [
    selectedAuditTable,
    selectedPageSize,
    selectedDateRange,
    selectedSourceId,
    selectedActorUserId,
  ]);

  const auditQueryUrlWithCursor = useMemo(() => {
    if (!auditsResponse?.nextCursor) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('cursor', auditsResponse?.nextCursor);
    return `${auditQueryUrl}&${params.toString()}`;
  }, [auditQueryUrl, auditsResponse?.nextCursor]);

  const runSearch = useCallback(
    async (url: string): Promise<Result<GetAuditsResponseDto, BaseError>> => {
      const result = await userAuthenticatedApiCall({
        responseSchema: GetAuditsResponseDto,
        url,
        requestOptions: {
          method: 'GET',
        },
      });

      if (!result) {
        return errResult({
          errorType: 'UnknownError',
          errorReason: 'An unknown error occurred while making the request',
        });
      }

      if (result.resultType === 'err') {
        return result;
      }

      return okResult(result.value);
    },
    [userAuthenticatedApiCall],
  );

  const onSearch = useAsyncCallback(
    async (isAlive, useCursor: boolean) => {
      if (!isAlive() || isSearching) {
        return;
      }

      if (useCursor && !auditQueryUrlWithCursor) {
        setErrorMessage('Failed to build paginated request url');
        return;
      }

      setErrorMessage(null);
      setIsSearching(true);

      const url =
        useCursor && auditQueryUrlWithCursor
          ? auditQueryUrlWithCursor
          : auditQueryUrl;
      const result = await runSearch(url);
      if (result.resultType === 'err') {
        const errorReason =
          result.error.errorReason &&
          typeof result.error.errorReason === 'string'
            ? result.error.errorReason
            : 'An error occurred';

        setErrorMessage(errorReason);
        setIsSearching(false);
        return;
      }

      setIsSearching(false);
      setAuditsResponse(result.value);
    },
    [isSearching, auditQueryUrlWithCursor, auditQueryUrl, runSearch],
  );

  useEffect(() => {
    onSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Memoized table rows for the organizations
   */
  const rows = useMemo(() => {
    if (isSearching || !auditsResponse) {
      return null;
    }

    const actualRows: AdminTableRow[] = [];

    for (const audit of auditsResponse.audits) {
      const columns: AdminTableColumn[] = [
        {
          columnType: 'text',
          key: 'auditedAt',
          text: new Date(audit.auditedAt).toLocaleString(),
        },
        {
          columnType: 'text',
          key: 'auditOp',
          text: audit.auditOp,
        },
        {
          columnType: 'text',
          key: 'auditSessionUser',
          text: audit.auditSessionUser,
        },
        {
          columnType: 'text',
          key: 'auditActorRole',
          text: audit.auditActorRole ?? 'NULL',
        },
        {
          columnType: 'text',
          key: 'auditActorUserId',
          text: audit.auditActorUserId ?? 'NULL',
        },
        {
          columnType: 'text',
          key: 'sourceId',
          text: audit.sourceId,
        },
        {
          columnType: 'text',
          key: 'createdAt',
          text: audit.createdAt.toLocaleString(),
        },
        {
          columnType: 'text',
          key: 'updatedAt',
          text: audit.updatedAt.toLocaleString(),
        },
      ];

      switch (audit.auditTable) {
        case 'users':
          columns.push({
            columnType: 'text',
            key: 'cognitoUsername',
            text: audit.cognitoUsername,
          });
          columns.push({
            columnType: 'text',
            key: 'email',
            text: audit.email,
          });
          columns.push({
            columnType: 'text',
            key: 'isSuperAdmin',
            text: audit.isSuperAdmin ? language.yes() : language.no(),
          });
          columns.push({
            columnType: 'text',
            key: 'passwordResetToken',
            text: audit.passwordResetToken ?? 'NULL',
          });
          columns.push({
            columnType: 'text',
            key: 'passwordResetTokenExpiresAt',
            text: audit.passwordResetTokenExpiresAt?.toLocaleString() ?? 'NULL',
          });
          columns.push({
            columnType: 'text',
            key: 'enrollmentType',
            text: audit.enrollmentType,
          });
          break;
        case 'organizations':
          columns.push({
            columnType: 'text',
            key: 'name',
            text: audit.name,
          });
          columns.push({
            columnType: 'text',
            key: 'logoMimeType',
            text: audit.logoMimeType ?? 'NULL',
          });
          columns.push({
            columnType: 'text',
            key: 'logoUrl',
            text: audit.logoUrl ?? 'NULL',
          });
          break;
        case 'organizationUsers':
          columns.push({
            columnType: 'text',
            key: 'organizationId',
            text: audit.organizationId,
          });
          columns.push({
            columnType: 'text',
            key: 'userId',
            text: audit.userId,
          });
          break;
        case 'organizationUserRoles':
          columns.push({
            columnType: 'text',
            key: 'organizationUserId',
            text: audit.organizationUserId,
          });
          columns.push({
            columnType: 'text',
            key: 'role',
            text: audit.role,
          });
          break;
        case 'organizationGroups':
          columns.push({
            columnType: 'text',
            key: 'organizationId',
            text: audit.organizationId,
          });
          columns.push({
            columnType: 'text',
            key: 'name',
            text: audit.name,
          });
          columns.push({
            columnType: 'text',
            key: 'description',
            text: audit.description ?? 'NULL',
          });
          break;
        case 'organizationGroupUsers':
          columns.push({
            columnType: 'text',
            key: 'organizationGroupId',
            text: audit.organizationGroupId,
          });
          columns.push({
            columnType: 'text',
            key: 'organizationUserId',
            text: audit.organizationUserId,
          });
          break;
        case 'organizationGroupEntitlements':
          columns.push({
            columnType: 'text',
            key: 'organizationGroupId',
            text: audit.organizationGroupId,
          });
          columns.push({
            columnType: 'text',
            key: 'databaseId',
            text: audit.databaseId,
          });
          break;
        case 'organizationGroupEntitlementFeatures':
          columns.push({
            columnType: 'text',
            key: 'organizationGroupEntitlementId',
            text: audit.organizationGroupEntitlementId,
          });
          columns.push({
            columnType: 'text',
            key: 'featureKey',
            text: audit.key,
          });
          columns.push({
            columnType: 'text',
            key: 'focusArea',
            text: audit.focusArea ?? 'NULL',
          });
          break;
        case 'organizationGroupEntitlementDataRules':
          columns.push({
            columnType: 'text',
            key: 'organizationGroupEntitlementId',
            text: audit.organizationGroupEntitlementId,
          });
          columns.push({
            columnType: 'text',
            key: 'description',
            text: audit.description ?? 'NULL',
          });
          columns.push({
            columnType: 'text',
            key: 'dataRule',
            text: JSON.stringify(audit.rule, null, 2),
          });
          break;
        case 'dashboards':
          columns.push({
            columnType: 'text',
            key: 'name',
            text: audit.name,
          });
          columns.push({
            columnType: 'text',
            key: 'ownerUserId',
            text: audit.ownerUserId,
          });
          columns.push({
            columnType: 'text',
            key: 'organizationId',
            text: audit.organizationId,
          });
          columns.push({
            columnType: 'text',
            key: 'databaseId',
            text: audit.databaseId,
          });
          break;
        case 'dashboardCharts':
          columns.push({
            columnType: 'text',
            key: 'name',
            text: audit.name,
          });
          columns.push({
            columnType: 'text',
            key: 'dashboardId',
            text: audit.dashboardId,
          });
          columns.push({
            columnType: 'text',
            key: 'customVizRequest',
            text: JSON.stringify(audit.customVizRequest, null, 2),
          });
          columns.push({
            columnType: 'text',
            key: 'description',
            text: audit.description ?? 'NULL',
          });
          columns.push({
            columnType: 'text',
            key: 'positionOnDashboard',
            text: String(audit.positionOnDashboard),
          });
          columns.push({
            columnType: 'text',
            key: 'positionOnRow',
            text: String(audit.positionOnRow),
          });
          break;
        case 'dashboardExternalUsers':
          columns.push({
            columnType: 'text',
            key: 'dashboardId',
            text: audit.dashboardId,
          });
          columns.push({
            columnType: 'text',
            key: 'email',
            text: audit.email,
          });
          columns.push({
            columnType: 'text',
            key: 'state',
            text: audit.state,
          });
          columns.push({
            columnType: 'text',
            key: 'invitedByUserId',
            text: audit.invitedByUserId,
          });
          columns.push({
            columnType: 'text',
            key: 'lastInvitationSentAt',
            text: audit.lastInvitationSentAt?.toISOString() ?? 'NULL',
          });
          columns.push({
            columnType: 'text',
            key: 'revokedAt',
            text: audit.revokedAt?.toISOString() ?? 'NULL',
          });
          columns.push({
            columnType: 'text',
            key: 'revokedByUserId',
            text: audit.revokedByUserId ?? 'NULL',
          });
          break;
        case 'organizationPrivateNetworkingEnablement':
          columns.push({
            columnType: 'text',
            key: 'organizationId',
            text: audit.organizationId,
          });
          columns.push({
            columnType: 'text',
            key: 'enabled',
            text: audit.enabled ? language.yes() : language.no(),
          });
          columns.push({
            columnType: 'text',
            key: 'whitelistedHosts',
            text: audit.whitelistedHosts.join(', '),
          });
          break;
        case 'databaseFeatureFlags':
          columns.push({
            columnType: 'text',
            key: 'databaseId',
            text: audit.databaseId,
          });
          columns.push({
            columnType: 'text',
            key: 'feature',
            text: audit.key,
          });
          columns.push({
            columnType: 'text',
            key: 'enabled',
            text: audit.enabled ? language.yes() : language.no(),
          });
          break;
        default:
          throw new Error('Unexpected audit table');
      }

      actualRows.push({
        key: audit.id,
        columns,
      });
    }
    return actualRows;
  }, [isSearching, auditsResponse, language]);

  const headings = useMemo(() => {
    const actualHeadings = [
      language.auditsPageAuditTimestampHeading(),
      language.auditsPageAuditOperationHeading(),
      language.auditsPageAuditSessionUserHeading(),
      language.auditsPageAuditActorRoleHeading(),
      language.auditsPageAuditActorUserIdHeading(),
      language.auditsPageSourceIdHeading(),
      language.auditsPageCreatedAtHeading(),
      language.auditsPageUpdatedAtHeading(),
    ];

    if (isSearching || !auditsResponse || auditsResponse.audits.length === 0) {
      return actualHeadings;
    }

    const auditTable = auditsResponse.audits[0].auditTable;

    switch (auditTable) {
      case 'users':
        actualHeadings.push(language.auditsPageCognitoUsernameHeading());
        actualHeadings.push(language.auditsPageEmailHeading());
        actualHeadings.push(language.auditsPageIsSuperAdminHeading());
        actualHeadings.push(language.auditsPagePasswordResetTokenHeading());
        actualHeadings.push(
          language.auditsPagePasswordResetTokenExpiresAtHeading(),
        );
        actualHeadings.push(language.auditsPageEnrollmentTypeHeading());
        break;
      case 'organizations':
        actualHeadings.push(language.auditsPageNameHeading());
        actualHeadings.push(language.auditsPageLogoMimeTypeHeading());
        actualHeadings.push(language.auditsPageLogoUrlHeading());
        break;
      case 'organizationUsers':
        actualHeadings.push(language.auditsPageOrganizationIdHeading());
        actualHeadings.push(language.auditsPageUserIdHeading());
        break;
      case 'organizationUserRoles':
        actualHeadings.push(language.auditsPageOrganizationUserIdHeading());
        actualHeadings.push(language.auditsPageRoleHeading());
        break;
      case 'organizationGroups':
        actualHeadings.push(language.auditsPageOrganizationIdHeading());
        actualHeadings.push(language.auditsPageNameHeading());
        actualHeadings.push(language.auditsPageDescriptionHeading());
        break;
      case 'organizationGroupUsers':
        actualHeadings.push(language.auditsPageOrganizationGroupIdHeading());
        actualHeadings.push(language.auditsPageOrganizationUserIdHeading());
        break;
      case 'organizationGroupEntitlements':
        actualHeadings.push(language.auditsPageOrganizationGroupIdHeading());
        actualHeadings.push(language.auditsPageDatabaseIdHeading());
        break;
      case 'organizationGroupEntitlementFeatures':
        actualHeadings.push(language.auditsPageEntitlementIdHeading());
        actualHeadings.push(language.auditsPageFeatureKeyHeading());
        actualHeadings.push(language.auditsPageFocusAreaHeading());
        break;
      case 'organizationGroupEntitlementDataRules':
        actualHeadings.push(language.auditsPageEntitlementIdHeading());
        actualHeadings.push(language.auditsPageDescriptionHeading());
        actualHeadings.push(language.auditsPageDataRuleHeading());
        break;
      case 'dashboards':
        actualHeadings.push(language.auditsPageNameHeading());
        actualHeadings.push(language.auditsPageOwnerUserIdHeading());
        actualHeadings.push(language.auditsPageOrganizationIdHeading());
        actualHeadings.push(language.auditsPageDatabaseIdHeading());
        break;
      case 'dashboardCharts':
        actualHeadings.push(language.auditsPageNameHeading());
        actualHeadings.push(language.auditsPageDashboardIdHeading());
        actualHeadings.push(language.auditsPageCustomVizHeading());
        actualHeadings.push(language.auditsPageDescriptionHeading());
        actualHeadings.push(language.auditsPagePositionOnDashboardHeading());
        actualHeadings.push(language.auditsPagePositionOnRowHeading());
        break;
      case 'dashboardExternalUsers':
        actualHeadings.push(language.auditsPageDashboardIdHeading());
        actualHeadings.push(language.auditsPageEmailHeading());
        actualHeadings.push(language.auditsPageStateHeading());
        actualHeadings.push(language.auditsPageInvitedByUserIdHeading());
        actualHeadings.push(language.auditsPageLastInvitationSentAtHeading());
        actualHeadings.push(language.auditsPageRevokedAtHeading());
        actualHeadings.push(language.auditsPageRevokedByUserIdHeading());
        break;
      case 'organizationPrivateNetworkingEnablement':
        actualHeadings.push(language.auditsPageOrganizationIdHeading());
        actualHeadings.push(language.auditsPageEnabledHeading());
        actualHeadings.push(language.auditsPageWhitelistedHostsHeading());
        break;
      case 'databaseFeatureFlags':
        actualHeadings.push(language.auditsPageDatabaseIdHeading());
        actualHeadings.push(language.auditsPageFeatureKeyHeading());
        actualHeadings.push(language.auditsPageEnabledHeading());
        break;
      default:
        throw guardExhaustive(auditTable);
    }

    return actualHeadings;
  }, [language, isSearching, auditsResponse]);

  return (
    <AdminLayout currentPage={'Audits'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Audits', to: '/audits' },
        ]}
      />
      <S2Typography
        variant="body-6"
        translatedContent={language.auditsPageSubHeading()}
      />

      {!isSearching && rows !== null ? (
        <>
          <S2Typography
            variant="headline-4"
            translatedContent={language.auditsPageTitle()}
          />
          <Spacing direction="vertical" size="sm">
            <Spacing direction="horizontal" size="sm">
              <SelectBoxWithSearch
                type={'single'}
                label={language.auditsPageAuditTableLabel()}
                selected={selectedAuditTable}
                setSelected={(value) => setSelectedAuditTable(value)}
                list={AuditTableQueryParam.options.map((o) => ({
                  optionLabel: o.value,
                  optionValue: o.value,
                }))}
              />
              <SelectBoxWithSearch
                type={'single'}
                label={language.auditsPagePageSizeLabel()}
                selected={selectedPageSize}
                setSelected={(value) => setSelectedPageSize(value)}
                list={[
                  {
                    optionLabel: '1',
                    optionValue: '1',
                  },
                  {
                    optionLabel: '10',
                    optionValue: '10',
                  },
                  {
                    optionLabel: '25',
                    optionValue: '25',
                  },
                  {
                    optionLabel: '50',
                    optionValue: '50',
                  },
                  {
                    optionLabel: '75',
                    optionValue: '75',
                  },
                  {
                    optionLabel: '100',
                    optionValue: '100',
                  },
                  {
                    optionLabel: '1000',
                    optionValue: '1000',
                  },
                  {
                    optionLabel: 'All',
                    optionValue: 'all',
                  },
                ]}
              />
              <Spacing
                direction="vertical"
                size="sm"
                className="justify-center"
              >
                <S2DateRangePicker
                  value={selectedDateRange}
                  onChange={setSelectedDateRange}
                  label="default"
                  outline={true}
                />
              </Spacing>
            </Spacing>
            <Spacing direction="horizontal" size="sm">
              <SimpleInput
                label={language.auditsPageSourceIdHeading()}
                value={selectedSourceId}
                onChange={setSelectedSourceId}
              />
              <SimpleInput
                label={language.auditsPageAuditActorUserIdHeading()}
                value={selectedActorUserId}
                onChange={setSelectedActorUserId}
              />
              <PrimaryButtonLightBg
                onClick={() => onSearch(false)}
                translatedContent={language.search()}
                disabled={isSearching}
              />
            </Spacing>
          </Spacing>
          <Spacing direction="vertical" size="sm">
            <AdminTable headings={headings} rows={rows} />
            {auditsResponse?.nextCursor ? (
              <SecondaryButtonLightBg
                onClick={() => onSearch(true)}
                translatedContent={language.nextPage()}
                disabled={isSearching}
              />
            ) : (
              <></>
            )}
          </Spacing>
        </>
      ) : (
        <Spacing direction="horizontal" size="sm">
          <Spinner size={24} />
        </Spacing>
      )}

      {/* Error message modal */}
      <Transition appear show={!!errorMessage} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-overlay"
          onClose={() => setErrorMessage(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Error
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{errorMessage}</p>
                  </div>

                  <div className="mt-4">
                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={() => setErrorMessage(null)}
                      className="mt-4"
                    >
                      Close
                    </LegacyButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AdminLayout>
  );
};
